// useSip — the SIP.js glue for the browser softphone.
//
// Wraps the sip.js UserAgent / Registerer / Inviter lifecycle behind a small
// React API the softphone UI can drive (placeCall, answer, hangup, mute, hold,
// DTMF, transfer). Real telephony is handled entirely by sip.js; this hook just
// makes it React-friendly and manages the remote-audio element.
//
// Two modes, chosen by config:
//   - config.wss present  → REAL: register with Asterisk over WSS, place/receive
//     WebRTC calls (DTLS-SRTP). Needs the PBX + per-agent creds + coturn.
//   - config null/no wss   → MOCK: local simulation so the page works as a design
//     preview until the PBX (docs/live-agent-softphone.md) is provisioned.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  UserAgent,
  Registerer,
  Inviter,
  Invitation,
  SessionState,
  type Session,
} from "sip.js";
import { getActiveTenantSlug, opsFetch, readCsrfCookie, tenantScopedUrl } from "../../api";
import { startRing, stopRing, ringerBlocked } from "./ringtone";
import { startLiveTranscribe, type LiveTurn } from "./live-transcribe";
import { MIC_BLOCKED_MESSAGE, isMicUnavailableError, micStatusFromPermission, type MicStatus } from "./mic-permission";

// ── call → platform ─────────────────────────────────────────────────────────
// Announce this agent's calls to Helix so a human's inbound/outbound lands on
// the same surfaces an AI call does — Real-time Call Logs while it is up, then
// Recordings & Transcripts once it ends. Without this the softphone is a closed
// loop in the browser and a real call leaves no record anywhere.
//
// Posted to the AUTHENTICATED ops endpoint: the tenant is derived from the ops
// session server-side, never from this payload. The public
// POST /api/voice/:provider/events ingress is unauthenticated and must not be
// reachable from a browser.
//
// Fire-and-forget by contract — telephony must never depend on it. A failed
// report costs a row in a dashboard, never the call.
function reportCall(body: Record<string, unknown>): void {
  void opsFetch("/ops/api/voice/softphone/events", { method: "POST", json: body }).catch(
    (e: unknown) => console.warn("[softphone] call not recorded:", e),
  );
}

/**
 * Send a SIP request and ignore it losing a race with the dialog.
 *
 * bye() / cancel() / reject() all return PROMISES, and they REJECT when the
 * dialog has already moved on — "Invalid session state Terminating" is the usual
 * one, thrown when the far end hung up a moment before the agent did, or when
 * End is pressed twice. A synchronous try/catch cannot see a rejected promise,
 * so every one of those escaped as an unhandled rejection and surfaced in the
 * console as an uncaught error out of inviter.js.
 *
 * Losing that race is normal and harmless — the call is ending either way and
 * there is nothing left to send. The NOISE is what is harmful: a console full of
 * red from an ordinary hang-up is how a real telephony error goes unnoticed.
 *
 * The synchronous try/catch at each call site STAYS. SIP.js also throws
 * synchronously for an illegal request, so both paths have to be covered.
 */
function settle(p: unknown): void {
  const maybe = p as Promise<unknown> | undefined;
  if (maybe && typeof maybe.catch === "function") {
    void maybe.catch(() => { /* the dialog moved on first */ });
  }
}

/**
 * Did the callee actually pick up?
 *
 * OUTBOUND ONLY, and only because the dialplan runs Answer() before Dial() to
 * dodge a WebRTC early-media bug. That makes SessionState.Established arrive the
 * instant the agent presses dial, so the browser genuinely cannot tell a ringing
 * call from a connected one — it asks the server, which learns the truth from
 * the PBX's ${DIALSTATUS} (see server/voice/dial-result.ts).
 *
 * Polled rather than read once: the PBX reports from its hangup handler at the
 * same moment the browser sees the session terminate, and that report has to
 * cross a tunnel and reach the database before it can be read back. Measured
 * round-trip is ~2s, so ten attempts over ~5s leaves real margin. A wrap-up
 * dialog arriving a second late is imperceptible; one that should never have
 * opened is the bug this exists to prevent.
 *
 * DEFAULTS TO TRUE on error or timeout, deliberately. A missing answer is not
 * evidence a call failed — a PBX that has not been given the dialplan change
 * yet reports nothing at all — and suppressing wrap-up on silence would lose
 * dispositions for real conversations. Wrong-but-recoverable beats data loss.
 */
async function confirmAnswered(callId: string): Promise<boolean> {
  for (let attempt = 0; attempt < 10; attempt++) {
    try {
      const r = await opsFetch<{ answered: boolean | null }>(
        `/ops/api/voice/softphone/call-result?callId=${encodeURIComponent(callId)}`,
      );
      if (r.answered === true) return true;
      if (r.answered === false) return false;
    } catch {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  return true;
}

/**
 * Report the end of a call while the page is being torn down.
 *
 * A normal reportCall() is dropped the moment the tab unloads, so a refresh or a
 * closed tab mid-call left the interaction stuck at `in_progress` with a null
 * ended_at — a ghost row sitting in Active Calls forever, because nothing
 * server-side closes helix calls (the staleness sweep in voice-dograh-sync only
 * covers Dograh).
 *
 * `keepalive` is what makes this survive unload. sendBeacon would too, but it
 * cannot set the CSRF header the ops API requires.
 */
function reportCallOnUnload(body: Record<string, unknown>): void {
  try {
    const csrf = readCsrfCookie() ?? undefined;
    void fetch(tenantScopedUrl("/ops/api/voice/softphone/events"), {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json", ...(csrf ? { "X-Ops-CSRF": decodeURIComponent(csrf) } : {}) },
      body: JSON.stringify(body),
    });
  } catch { /* the page is going away; nothing useful to do */ }
}

export interface SipConfig {
  /** wss://pbx.helixai.cx/ws — empty/undefined selects MOCK mode. */
  wss?: string;
  /** SIP domain, e.g. pbx.helixai.cx */
  domain: string;
  /** Agent extension, e.g. "1001" */
  ext: string;
  /** Short-lived agent password (fetched from Helix, never hard-coded). */
  password: string;
  /** STUN/TURN for NAT traversal (coturn). */
  iceServers?: RTCIceServer[];
}

export type SipStatus =
  | "offline"
  | "connecting"
  | "ready"
  | "ringing"
  | "oncall"
  | "wrap"
  | "error";

export interface CallInfo {
  name?: string;
  number: string;
  direction: "in" | "out";
}

export interface SipApi {
  status: SipStatus;
  registered: boolean;
  mock: boolean;
  error: string | null;
  call: CallInfo | null;
  incoming: CallInfo | null;
  muted: boolean;
  held: boolean;
  /**
   * The call is up and no audio is passing.
   *
   * Reported because it is the one fault the agent cannot see: SIP is fine, the
   * timer is running, the controls all work — and the customer hears nothing.
   * The desk has had a state for exactly this ("Audio path lost") since it was
   * built, but nothing ever set it, so it could only be reached from the preview
   * switch. ICE was already being watched here; it just stopped at a console
   * line and an error string.
   */
  mediaLost: boolean;
  /** The last ring was silent - the browser is blocking audio until the agent
   *  interacts with the page. See ringtone.ts. */
  ringerSilent: boolean;
  /** Whether the browser will give this desk the microphone. "blocked" means a
   *  call would connect with the agent silent, so the desk must say so before
   *  one arrives, not after (TC-A01.2). See mic-permission.ts. */
  micStatus: MicStatus;
  /** Forget the last error once the agent has read it. */
  clearError: () => void;
  /**
   * What is being said, as it is said.
   *
   * Each turn carries the leg it arrived on, so the speaker is a fact rather
   * than a guess — the two call legs are streamed separately (live-transcribe.ts)
   * instead of being mixed and diarized after the fact. The trailing turn per
   * leg may be interim and is replaced in place until it is final.
   */
  liveTurns: LiveTurn[];
  /** The interaction the live transcript is attributed to, once the server has
   *  resolved it. This is the key live-assist coaching arrives under. */
  assistConversationId: string | null;
  placeCall: (number: string, name?: string) => Promise<void>;
  answer: () => Promise<void>;
  reject: () => void;
  hangup: () => void;
  setMute: (on: boolean) => void;
  setHold: (on: boolean) => void | Promise<void>;
  sendDtmf: (digit: string) => void;
  /** Blind transfer: hand the caller over and drop out immediately. */
  transfer: (target: string) => void;
  // Warm (attended) transfer + conference.
  //
  // Blind transfer throws the caller at someone who may not be there, may be on
  // another call, or may know nothing about the problem — so the caller
  // re-explains everything to a stranger, or lands in a dead extension. A warm
  // transfer lets the agent speak to the colleague FIRST, with the caller on
  // hold, and only then connect them.
  /** The consultation call, while one is up. Null otherwise. */
  consult: CallInfo | null;
  /** True once the consulted party has actually answered. */
  consultLive: boolean;
  /** Put the caller on hold and ring a colleague or number. */
  startConsult: (target: string, name?: string) => Promise<void>;
  /** Connect caller to colleague and drop out (REFER with Replaces). */
  completeTransfer: () => void;
  /** Abandon the consultation and go back to the caller. */
  cancelConsult: () => Promise<void>;
  /** Which call the agent is listening to right now. */
  activeLine: "customer" | "consult";
  /** Move between the two live calls, holding the one being left. */
  switchLine: (to: "customer" | "consult") => Promise<void>;
  /** Put all three into one conference on the PBX. */
  conference: () => Promise<void>;
  /** The Helix interaction id for the CURRENT call, so UI that acts on the call
   *  (wrap-up disposition) can address the right row. Null when idle. */
  currentCallId: string | null;
  /** The id of the last call that ACTUALLY CONNECTED and has now ended — the
   *  only thing there is to wrap up. Set on Terminated only when the session
   *  reached Established, cleared when the next call connects. Null means there
   *  is nothing to disposition, so the dialog must not open.
   *
   *  This is explicit rather than inferred because inferring it from
   *  "`call` went from set to null while `currentCallId` was set" kept opening
   *  the wrap-up dialog for calls that never connected. */
  wrapUpCallId: string | null;
  /** Mock-mode only: fake an inbound call so the preview can demo the ring. */
  simulateIncoming: (info: CallInfo) => void;
}

/** Pull the RTCPeerConnection out of a sip.js Web session (typed as unknown). */
/** How long a decline stands the agent down before they go Ready again. Long
 *  enough that the queue stops re-offering the call it just refused, short
 *  enough that nobody is quietly out of the queue. See reject(). */
const DECLINE_PAUSE_MS = 30_000;

function peerConn(session: Session): RTCPeerConnection | null {
  const sdh = session.sessionDescriptionHandler as unknown as {
    peerConnection?: RTCPeerConnection;
  } | undefined;
  return sdh?.peerConnection ?? null;
}

export function useSip(config: SipConfig | null): SipApi {
  const mock = !config?.wss;

  const [status, setStatus] = useState<SipStatus>("offline");
  const [registered, setRegistered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [call, setCall] = useState<CallInfo | null>(null);
  const [incoming, setIncoming] = useState<CallInfo | null>(null);
  const [muted, setMuted] = useState(false);
  const [mediaLost, setMediaLost] = useState(false);
  // True when a call rang but the browser would not play the tone. Cleared on
  // the next gesture, because that is what unblocks the audio.
  const [ringerSilent, setRingerSilent] = useState(false);
  const [micStatus, setMicStatus] = useState<MicStatus>("unknown");
  const [held, setHeld] = useState(false);
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  const [wrapUpCallId, setWrapUpCallId] = useState<string | null>(null);
  const [consult, setConsult] = useState<CallInfo | null>(null);
  const [consultLive, setConsultLive] = useState(false);
  /** Which of the two calls the agent is actually listening to. Only meaningful
   *  while a consultation is up; "customer" whenever it is not. */
  const [activeLine, setActiveLine] = useState<"customer" | "consult">("customer");

  const uaRef = useRef<UserAgent | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const invitationRef = useRef<Invitation | null>(null);
  /** The second, consultative call during a warm transfer. */
  const consultRef = useRef<Session | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /** Its own audio element. Reusing the primary one would overwrite the held
   *  caller's stream, so ending the consultation would leave the agent on a live
   *  call with no audio — indistinguishable from a broken call. */
  const consultAudioRef = useRef<HTMLAudioElement | null>(null);
  /** The id we reported this call under, so actions taken DURING the call
   *  (transfer) can be stamped onto the same interaction row. */
  const callIdRef = useRef<string | null>(null);
  /** The extension the live call belongs to — needed by the unload handler,
   *  which cannot read React state at teardown time. */
  const callExtRef = useRef<string | null>(null);
  /** Call id the PBX supplied on an inbound INVITE (X-Helix-Call-Id), adopted
   *  so PBX recordings and Helix interactions share one key. */
  const inboundCallIdRef = useRef<string | null>(null);
  /** Live transcript streams, torn down with the call. Separate from the
   *  recorder: that produces the archive, this produces the words on screen
   *  while they are still being said. */
  const liveTranscribeRef = useRef<{ stop: () => void } | null>(null);
  const [liveTurns, setLiveTurns] = useState<LiveTurn[]>([]);
  /** The interaction id the transcript socket resolved this call to — the key
   *  the live-assist bus publishes coaching under. Null until the socket says
   *  so, and cleared with the call. */
  const [assistConversationId, setAssistConversationId] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recCtxRef = useRef<AudioContext | null>(null);

  // Close the interaction if the tab goes away mid-call. Without this a refresh
  // or a closed tab leaves a permanently "in progress" row in Active Calls,
  // because the sip.js Terminated listener never runs. `pagehide` fires in cases
  // `beforeunload` misses (bfcache, mobile), so it is the one to hook.
  useEffect(() => {
    const onHide = () => {
      const id = callIdRef.current;
      const ext = callExtRef.current;
      if (id && ext) reportCallOnUnload({ event: "call.ended", callId: id, ext });

      // AND TELL THE SWITCH, NOT ONLY HELIX.
      //
      // This closed the interaction row and said nothing to Asterisk, so the
      // channel stayed up on the PBX with nobody on the other end of it. The
      // leftovers were real and long-lived: a ChanSpy still attached after 26
      // hours, conference rooms holding legs from calls that ended in the
      // morning, an agent's channel bridged to a customer who had hung up. They
      // hold a licence slot each, they show in `core show channels` as live
      // traffic, and the only cure was somebody noticing and killing them by
      // hand.
      //
      // Best effort by nature — the tab is going away and there is no promise
      // the BYE reaches the wire. Each is wrapped so a failing one cannot stop
      // the next: the consultation leg matters as much as the primary, and
      // stopping the UA closes the transport, which is what makes Asterisk drop
      // anything still standing.
      try { settle((sessionRef.current as unknown as { bye?: () => Promise<unknown> })?.bye?.()); }
      catch { /* not established, or already gone */ }
      try { settle((consultRef.current as unknown as { bye?: () => Promise<unknown> })?.bye?.()); }
      catch { /* no consultation up */ }
      try { void uaRef.current?.stop(); } catch { /* transport already closing */ }
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  // A single hidden <audio> for the remote leg.
  const remoteAudio = useCallback((): HTMLAudioElement => {
    if (!audioRef.current) {
      const el = document.createElement("audio");
      el.autoplay = true;
      el.hidden = true;
      document.body.appendChild(el);
      audioRef.current = el;
    }
    return audioRef.current;
  }, []);

  const attachMedia = useCallback((session: Session) => {
    const pc = peerConn(session);
    if (!pc) return;
    const stream = new MediaStream();
    pc.getReceivers().forEach((r) => r.track && stream.addTrack(r.track));
    remoteAudio().srcObject = stream;

    // ── ICE observability ────────────────────────────────────────────────────
    // A WebRTC call that drops a few seconds after being answered is almost
    // always ICE, not SIP: no working candidate pair is found, the connection
    // goes to `failed`, and the session is torn down. Without this, that is
    // completely silent — the agent just sees the call vanish, and the SIP trace
    // looks clean because the signalling really was fine.
    //
    // The usual cause is having STUN but no TURN. STUN only tells the browser
    // its public address; when both ends are behind restrictive NAT there is no
    // direct path and a relay is the only option. See docs/live-agent-softphone.md
    // §11 and docs/telephony-asterisk-sipjs.md §9 — both list coturn as required.
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      console.info("[softphone] ICE:", st);
      // BOTH of these mean the customer cannot hear the agent, so both raise the
      // desk's "Audio path lost" state rather than only writing to the console.
      // `disconnected` is often transient and may recover, which is exactly why
      // it belongs on screen: the agent should stop talking and check, instead of
      // finishing a sentence into a dead line.
      if (st === "failed") {
        setMediaLost(true);
        setError(
          "Media path failed (ICE). The call was connected but no audio route could be established — " +
          "this usually means a TURN server is needed for this network.",
        );
      } else if (st === "disconnected") {
        setMediaLost(true);
        console.warn("[softphone] ICE disconnected — media interrupted; the browser will drop the call if it does not recover");
      } else if (st === "connected" || st === "completed") {
        // Recovered, or never lost. Clearing matters as much as setting: a stripe
        // stuck on "Audio path lost" after the path came back is the same lie in
        // the other direction.
        setMediaLost(false);
        // Report which kind of candidate pair actually won. 'relay' means TURN
        // was required; 'host'/'srflx' means a direct path worked.
        void pc.getStats().then((stats) => {
          stats.forEach((r: any) => {
            if (r.type === "candidate-pair" && r.state === "succeeded" && r.nominated) {
              const local = stats.get(r.localCandidateId) as any;
              console.info(`[softphone] media path: ${local?.candidateType ?? "?"} (relay = TURN was needed)`);
            }
          });
        }).catch(() => { /* stats are best-effort */ });
      }
    };
  }, [remoteAudio]);

  // ── Call recording, captured in the browser — THE FALLBACK, not the design ──
  //
  // ⚠ READ BEFORE RELYING ON THIS. Browser capture only records calls a browser
  // is a party to. It therefore records:
  //   • NOTHING for a call handled by an AI agent (no browser in the call)
  //   • only the FIRST PART of a transferred call — the moment the agent hands
  //     off, this leg ends and the recording stops while the call continues
  // A recording that ends at the transfer is worse than none, because it looks
  // complete.
  //
  // The correct source is MixMonitor on the PBX: Asterisk is in the media path
  // for every call, human or AI, before and after a transfer. That path is
  // implemented — see server/voice/asterisk/pbx/{extensions.conf,
  // rt-recording-upload.sh} and server/voice/recording-ingest.ts — and needs
  // deploying to the PBX. Once it is, a PBX recording overwrites whatever this
  // captured, because both write voice_interactions.recording_url and the PBX
  // upload lands later.
  //
  // Until then this covers the human-to-human case, which is what the live desk
  // does today. Both legs are mixed through a Web Audio graph: the remote track
  // alone gives only the customer, the mic alone only the agent.
  /**
   * Live transcript for this call. Started beside the recorder, from the same
   * peer connection, but reading the two legs SEPARATELY rather than mixed —
   * see live-transcribe.ts for why that removes the speaker guesswork the
   * post-call path has to live with.
   *
   * Wrapped and best-effort throughout: a call must never fail because nobody
   * could transcribe it.
   */
  const startLive = useCallback((session: Session) => {
    try {
      const pc = peerConn(session);
      if (!pc) return;
      const workspace = getActiveTenantSlug();
      if (!workspace) return;      // no scope to authorise the socket with
      setLiveTurns([]);
      setAssistConversationId(null);
      liveTranscribeRef.current = startLiveTranscribe({
        pc,
        workspace,
        // Lets the server attribute these turns to a call, which is what live
        // assist keys on. Read at call time rather than captured, because the
        // inbound path adopts the PBX's X-Helix-Call-Id after this ref is set.
        callId: callIdRef.current,
        onTurn: (t) => {
          setLiveTurns((prev) => {
            // Interim results REPLACE the leg's trailing interim; a final one
            // commits it. Appending both would print every half-sentence twice
            // and grow the panel by a line per word.
            const out = prev.slice();
            const lastIdx = out.map((x) => x.leg).lastIndexOf(t.leg);
            if (lastIdx >= 0 && !out[lastIdx].isFinal) out[lastIdx] = t;
            else out.push(t);
            return out.slice(-200);
          });
        },
        // The interaction the server resolved our call to. Live assist publishes
        // coaching under this id, so the desk needs it to show the rail for THIS
        // call rather than for every conversation in the workspace.
        onConversation: (id) => setAssistConversationId(id),
      });
    } catch {
      // No transcript. The call is unaffected.
    }
  }, []);

  const stopLive = useCallback(() => {
    try { liveTranscribeRef.current?.stop(); } catch { /* already gone */ }
    liveTranscribeRef.current = null;
  }, []);

  const startRecording = useCallback((session: Session) => {
    try {
      const pc = peerConn(session);
      if (!pc || typeof MediaRecorder === "undefined") return;

      const AudioCtor: typeof AudioContext =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtor) return;
      const ctx = new AudioCtor();
      const dest = ctx.createMediaStreamDestination();

      const add = (track: MediaStreamTrack | null | undefined) => {
        if (!track || track.kind !== "audio") return;
        ctx.createMediaStreamSource(new MediaStream([track])).connect(dest);
      };
      pc.getReceivers().forEach((r) => add(r.track));   // the far end
      pc.getSenders().forEach((s) => add(s.track));     // this agent

      const rec = new MediaRecorder(dest.stream);
      recChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      // Timeslice so a crash mid-call still leaves whatever was flushed, rather
      // than one buffer that is lost entirely.
      rec.start(5000);
      recorderRef.current = rec;
      recCtxRef.current = ctx;
    } catch (e) {
      console.warn("[softphone] recording could not start:", e);
    }
  }, []);

  const stopRecordingAndUpload = useCallback((callId: string, ext: string) => {
    const rec = recorderRef.current;
    const ctx = recCtxRef.current;
    recorderRef.current = null;
    recCtxRef.current = null;
    if (!rec) return;
    rec.onstop = () => {
      const chunks = recChunksRef.current;
      recChunksRef.current = [];
      void ctx?.close().catch(() => { /* ignore */ });
      if (chunks.length === 0) return;
      const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
      const csrf = readCsrfCookie() ?? undefined;
      // Fire-and-forget, like every other report: a failed upload costs the
      // recording, never the call (which has already ended by this point).
      void fetch(
        tenantScopedUrl(`/ops/api/voice/softphone/recording?callId=${encodeURIComponent(callId)}&ext=${encodeURIComponent(ext)}`),
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": blob.type, ...(csrf ? { "X-Ops-CSRF": decodeURIComponent(csrf) } : {}) },
          body: blob,
        },
      ).then((r) => {
        if (!r.ok) console.warn("[softphone] recording upload rejected:", r.status);
        else console.info(`[softphone] recording uploaded (${Math.round(blob.size / 1024)} KB)`);
      }).catch((e) => console.warn("[softphone] recording upload failed:", e));
    };
    try { rec.stop(); } catch { /* already stopped */ }
  }, []);

  const wireSession = useCallback((session: Session, info: CallInfo, presetCallId?: string) => {
    sessionRef.current = session;
    setCall(info);
    setMuted(false);
    setHeld(false);
    // A new call starts with a clean media state. Without this a failed path on
    // the previous call would still be flagged on the next one, and the agent
    // would stop talking on a call that is perfectly fine.
    setMediaLost(false);
    // Both directions converge here, so this is the one place that has to know
    // about reporting. Reported on ESTABLISHED, not on invite: a call that rang
    // and was never answered isn't an interaction, and emitting on ring would
    // leave orphan "in progress" rows for every abandoned inbound.
    //
    // Outbound passes its id in (placeCall minted it early so it could ride the
    // INVITE as X-Helix-Call-Id, which is how PBX-side recording correlates).
    // Inbound has no such chance — the far end sent the INVITE — so it mints one
    // here and PBX recordings for inbound correlate on (extension, start time)
    // instead. See server/voice/asterisk/pbx/README.md.
    const callId = presetCallId ?? `sp_${crypto.randomUUID()}`;
    // A SUPERVISOR LISTENING IS NOT A CALL WITH A CUSTOMER.
    //
    // Monitoring rings the supervisor's own phone and attaches them to somebody
    // else's conversation. That leg is a real SIP session, so the desk reported
    // it like any other call — and every Listen, Whisper and Barge landed in
    // Real-time Call Logs as its own interaction, with the SUPERVISOR'S OWN
    // EXTENSION in the customer column. Four of them in one afternoon of
    // testing. Left alone it would inflate call volume, dilute AHT and put
    // minutes against an agent who was not on those calls.
    //
    // Recognised by the caller id spy.ts mints ("Monitor <ext>"), which is our
    // own string on both ends — if that format changes there, this must change
    // with it. The leg still works exactly as before: audio, controls, the
    // strip on Agents & states. It is only kept out of the RECORD, because it
    // is not one.
    const isMonitorLeg = /^Monitor\b/i.test(info.name ?? "");
    let reported = isMonitorLeg;   // "already reported" = never report

    // ── STRANDED-CALL WATCHDOG ────────────────────────────────────────────────
    //
    // A CALL ENDED ON EXACTLY ONE SIGNAL: a BYE arriving. That is fine while the
    // signalling path is healthy and a trap when it is not — and ours is not.
    // The PBX loads this extension from realtime, and rt-sync DELETES it on any
    // tick where it cannot reach this laptop (13k times in one day). If that
    // happens mid-call, Asterisk no longer has an AOR or a contact for the
    // agent: it tears down its own side and has nowhere to send the BYE.
    //
    // The browser then hears nothing. Terminated never fires, so `setCall(null)`
    // never runs, and the desk shows a live call that ended minutes ago — the
    // agent sits there believing somebody is on the line.
    //
    // ICE ALREADY KNOWS. attachMedia watches iceConnectionState and raises
    // "Audio path lost", but only as a message: it never ends anything. So the
    // one component that can tell the media is gone was reporting it and
    // stopping there.
    //
    // Ten seconds of continuously-bad ICE on an ESTABLISHED call is taken as the
    // call being over. `disconnected` alone is deliberately included even though
    // it often recovers — the counter resets the moment it does, so a blip costs
    // nothing, and ten seconds of silence is already too long to leave an agent
    // talking into a dead line.
    //
    // TERMINATION GOES THROUGH THE NORMAL PATH. bye() first; if the transport is
    // also gone that rejects harmlessly, so dispose() follows as the backstop.
    // Either way sip.js transitions to Terminated and the existing listener below
    // does the cleanup — wrap-up, call.ended, transcript flush. Nothing here
    // duplicates that logic, because two places clearing a call is how they drift.
    const STRANDED_TICKS = 10;
    let badTicks = 0;
    const watchdog = window.setInterval(() => {
      if (session.state !== SessionState.Established) return;
      const st = peerConn(session)?.iceConnectionState;
      if (st === "failed" || st === "disconnected" || st === "closed") {
        badTicks += 1;
        if (badTicks < STRANDED_TICKS) return;
        window.clearInterval(watchdog);
        console.warn(
          `[softphone] no media for ${STRANDED_TICKS}s (ICE ${st}) and no BYE — ending the call locally`,
        );
        settle(session.bye());
        window.setTimeout(() => {
          if (session.state !== SessionState.Terminated) {
            try { session.dispose(); } catch { /* already gone */ }
          }
        }, 2000);
      } else {
        badTicks = 0;
      }
    }, 1000);

    session.stateChange.addListener((state: SessionState) => {
      if (state === SessionState.Established) {
        setStatus("oncall");
        attachMedia(session);
        startRecording(session);
        // THE ID BEFORE THE SOCKET THAT NEEDS IT.
        //
        // startLive() reads callIdRef to tell the transcript socket which call
        // its audio belongs to — and this assignment used to sit three lines
        // BELOW that call, so the ref was still null (cleared when the previous
        // call ended) every single time. The socket therefore opened with no
        // callId, the server could not attribute the turns, nothing was
        // published to the assist bus, and live coaching could not run on any
        // call ever. The server log said it plainly once asked:
        // `live-ws leg=agent callId=(none) → interaction=(unresolved)`.
        callIdRef.current = isMonitorLeg ? null : callId;
        // No transcript for a monitoring leg either: it would stream the call a
        // supervisor is listening to a SECOND time, pay Deepgram twice for the
        // same audio, and have no interaction to attribute it to.
        if (!isMonitorLeg) startLive(session);
        if (config?.ext && !reported) {
          reported = true;
          callExtRef.current = config.ext;
          setCurrentCallId(callId);
          // A connected call supersedes any wrap-up still pending for the
          // previous one — the dialog's own rule was always "closes when the
          // next call begins".
          setWrapUpCallId(null);
          reportCall({
            event: "call.started",
            callId,
            ext: config.ext,
            direction: info.direction === "in" ? "inbound" : "outbound",
            from: info.direction === "in" ? info.number : config.ext,
            to: info.direction === "in" ? config.ext : info.number,
          });
        }
      } else if (state === SessionState.Terminated) {
        // A session that has already been REPLACED must not clear the UI.
        // During a conference the agent is moved onto the room's session while
        // the original call is still terminating; without this guard that
        // termination lands a moment later and wipes the live conference off
        // the screen, leaving the agent talking on a call the UI says ended.
        // The watchdog dies with the call it was watching — including when the
        // call ends normally, which is the common case. Left running it would
        // tick against a disposed session forever, one interval per call.
        window.clearInterval(watchdog);
        if (sessionRef.current && sessionRef.current !== session) return;
        sessionRef.current = null;
        setCall(null);
        setStatus("wrap");
        // Close the transcript sockets with the call. Deepgram is told to flush
        // first (server side), so the last words spoken still arrive.
        stopLive();
        // RETURN TO READY ONLY ONCE WE KNOW WHETHER THIS CALL IS WRAPPABLE.
        //
        // This used to be one unconditional 3s timer at the end of the handler,
        // and it RACED confirmAnswered, which polls the PBX for up to ~5s. On an
        // outbound call the order came out: wrap opens -> timer clears it, desk
        // drops to Ready -> poll lands, sets wrapUpCallId, wrap opens AGAIN with
        // a fresh 45s countdown. The agent was shown the disposition panel twice
        // for one call, and the second time arrived after they had looked away.
        //
        // Each branch below now starts the clear when its own outcome is known.
        const clearWrapSoon = () =>
          window.setTimeout(() => setStatus((s) => (s === "wrap" ? "ready" : s)), 3000);
        if (reported) {
          reportCall({ event: "call.ended", callId, ext: config!.ext });
          // `reported` is true only if this session reached Established, so this
          // is the single point at which a call becomes wrappable. The missed
          // branch below deliberately does NOT set it — and does not clear it
          // either, because an unanswered call must not discard a wrap-up still
          // owed for an earlier call that did connect.
          //
          // INBOUND is trustworthy as-is: wireSession only runs from answer(),
          // so an inbound session cannot reach Established unless the agent
          // picked up. OUTBOUND is not, because the dialplan answers the agent's
          // leg before it dials — so ask the PBX whether anyone was actually
          // there before asking the agent what the conversation was about.
          if (info.direction === "in") {
            // Known immediately — an inbound session only reaches Established
            // from answer().
            setWrapUpCallId(callId);
            clearWrapSoon();
          } else {
            // Not known until the PBX answers. Hold "wrap" until it does, so the
            // desk moves once rather than flickering through Ready.
            void confirmAnswered(callId).then((answered) => {
              if (answered) setWrapUpCallId(callId);
              clearWrapSoon();
            });
          }
          // Upload AFTER call.ended so the interaction row is already final when
          // the recording is attached to it.
          stopRecordingAndUpload(callId, config!.ext);
        } else if (config?.ext) {
          // NEVER ESTABLISHED — it rang and died. An outbound the agent cancelled,
          // or a callee who never picked up.
          //
          // This has to become a real interaction, for two reasons. It is the only
          // record that the attempt happened at all — previously an abandoned call
          // vanished completely. And without a row of its OWN, the wrap-up dialog
          // fell back to whatever `currentCallId` still held, which was the
          // PREVIOUS call: dispositioning here wrote the reason onto the wrong one.
          reportCall({
            event: "call.missed",
            callId,
            ext: config.ext,
            direction: info.direction === "in" ? "inbound" : "outbound",
            from: info.direction === "in" ? info.number : config.ext,
            to: info.direction === "in" ? config.ext : info.number,
          });
          // NO WRAP-UP FOR A CALL THAT NEVER CONNECTED. A disposition describes
          // how a conversation went, and there was no conversation — cancelling
          // your own outbound before the callee picks up has no outcome to record.
          //
          // Clearing rather than leaving it: currentCallId still holds the id of
          // the last ESTABLISHED call, and leaving it set is what made the dialog
          // open here and offer to disposition that earlier, unrelated call.
          setCurrentCallId(null);
          clearWrapSoon();
        } else {
          // No ext configured — nothing was reported either way.
          clearWrapSoon();
        }
        callIdRef.current = null;
        callExtRef.current = null;
        // wrapUpCallId is kept until the NEXT call starts so the disposition can
        // still be saved after hangup — clearing it here would make the buttons
        // dead exactly when the agent uses them.
      }
    });
  }, [attachMedia, config, startRecording, startLive, stopLive, stopRecordingAndUpload]);

  // ── connect + register (real mode) ──────────────────────────────────────
  useEffect(() => {
    if (mock) { setStatus("ready"); setRegistered(false); return; }
    const cfg = config as SipConfig;
    let ua: UserAgent | null = null;
    let registerer: Registerer | null = null;
    let cancelled = false;

    (async () => {
      try {
        setStatus("connecting");
        const uri = UserAgent.makeURI(`sip:${cfg.ext}@${cfg.domain}`);
        if (!uri) throw new Error("Invalid SIP URI");
        ua = new UserAgent({
          uri,
          transportOptions: { server: cfg.wss! },
          authorizationUsername: cfg.ext,
          authorizationPassword: cfg.password,
          logLevel: "debug",          // full WS/SIP flow in the browser console
          sessionDescriptionHandlerFactoryOptions: {
            peerConnectionConfiguration: { iceServers: cfg.iceServers ?? [] },
          },
          delegate: {
            onDisconnect: (err?: Error) => {
              if (cancelled) return;
              setRegistered(false);
              if (err) { setError("WebSocket lost: " + err.message); setStatus("error"); }
            },
            onInvite: (invitation: Invitation) => {
              invitationRef.current = invitation;
              const from = invitation.remoteIdentity;
              startRing();
              // A SILENT RING IS WORTH SAYING OUT LOUD. Chrome refuses audio
              // until the page has seen a gesture, so on a freshly loaded desk
              // the first call — the one that matters most — arrives with no
              // sound. The agent hears nothing and concludes the phone is
              // broken. Report it as state so the desk can show it; the tab
              // title flashes regardless (see ringtone.ts).
              setRingerSilent(ringerBlocked());
              // The caller can give up, or the PBX can time the Dial out, while
              // we are still alerting. Without this the ring played to its
              // 120s ceiling and the incoming-call card stayed on screen for a
              // call that no longer existed — the agent would "answer" a dead
              // invitation. Answer/reject clear these themselves; this covers
              // every ending we do NOT initiate.
              const callerNumber = from.uri.user ?? "unknown";
              invitation.stateChange.addListener((s: SessionState) => {
                if (s === SessionState.Terminated && invitationRef.current === invitation) {
                  stopRing();
                  invitationRef.current = null;
                  setIncoming(null);
                  setStatus((cur) => (cur === "ringing" ? "ready" : cur));
                  // DECLINED OR RANG OUT. answer() clears invitationRef, so reaching
                  // here with it still set means this call was never answered — the
                  // red button, or the caller giving up.
                  //
                  // wireSession never ran for this call, so nothing else will ever
                  // report it: no interaction row, no disposition, and the PBX's
                  // recording upload 404s forever against a call Helix never heard
                  // of. Reporting it here is what makes an abandoned call exist.
                  const missedId = inboundCallIdRef.current;
                  inboundCallIdRef.current = null;
                  // A monitoring leg that rang out is not a missed CUSTOMER
                  // call — nobody was waiting on the other end of it. Same rule
                  // as the answered path in wireSession: supervision stays out
                  // of the record entirely, in both directions.
                  const monitorRing = /^Monitor\b/i.test(from.displayName ?? "");
                  if (missedId && config?.ext && !monitorRing) {
                    reportCall({
                      event: "call.missed",
                      callId: missedId,
                      ext: config.ext,
                      direction: "inbound",
                      from: callerNumber,
                      to: config.ext,
                    });
                    // Recorded, but NOT offered for wrap-up: a call the agent
                    // never answered has no outcome to describe. Clearing the id
                    // also stops the dialog binding to the last answered call.
                    setCurrentCallId(null);
                  }
                }
              });
              // The PBX mints the call id for inbound (the carrier sent the
              // INVITE, so the browser had no chance to) and pushes it here as
              // X-Helix-Call-Id via the [helix-hdr] pre-dial handler. Adopting
              // it means the PBX recording and the Helix interaction share one
              // id in BOTH directions — no (ext, start-time) matching, which
              // breaks as soon as two calls overlap.
              inboundCallIdRef.current =
                invitation.request.getHeader("X-Helix-Call-Id")?.trim() || null;
              setIncoming({
                name: from.displayName || undefined,
                number: from.uri.user ?? "unknown",
                direction: "in",
              });
              setStatus("ringing");
            },
          },
        });
        // A build marker, so "did you reload?" stops being a guess. Bundle
        // filenames are content-hashed and source maps re-point every stack to
        // the same source lines, which means an old tab and a new one produce
        // identical-looking logs. This line does not.
        console.info("[softphone] build 2026-09-01b — incoming-call backdrop no longer declines");
        uaRef.current = ua;
        await ua.start();                 // connects the WSS transport
        if (cancelled) return;
        registerer = new Registerer(ua);
        // Truth comes from the registrar, not the (optimistic) call return.
        registerer.stateChange.addListener((s) => {
          if (cancelled) return;
          setRegistered(s === "Registered");
          if (s === "Registered") { setError(null); setStatus("ready"); }
        });
        await registerer.register({
          requestDelegate: {
            onReject: (response) => {
              if (!cancelled) {
                setError(`Register rejected: ${response.message.statusCode} ${response.message.reasonPhrase}`);
                setStatus("error");
              }
            },
          },
        });
      } catch (e) {
        if (!cancelled) { setError(e instanceof Error ? e.message : String(e)); setStatus("error"); }
      }
    })();

    return () => {
      cancelled = true;
      stopRing();   // never leave a ring playing over a torn-down session
      // End any consultation explicitly. A consult left hanging keeps a channel
      // up on the PBX — and if its far end is an application rather than a
      // phone (the echo test, an IVR), that channel never ends by itself. Five
      // of them survived an afternoon of testing.
      const pending = consultRef.current;
      consultRef.current = null;
      // The second line is gone, so the agent is on the first one whether or
      // not they chose to be. Leaving activeLine on "consult" would show L2 lit
      // with nothing behind it, and switchLine would then refuse to move back.
      setActiveLine("customer");
      if (pending) {
        try {
          if (pending.state === SessionState.Established) settle(pending.bye());
          else if (pending instanceof Inviter) settle(pending.cancel());
        } catch { /* already gone */ }
      }
      const reg = registerer;
      const agent = ua;
      // Tear down IN ORDER, and wait for the un-REGISTER to actually complete.
      //
      // unregister() sends REGISTER with Expires:0, which the PBX answers with a
      // 401 challenge expecting the request resent with a digest response. Firing
      // it without awaiting and then immediately calling ua.stop() kills the WSS
      // transport before that retry can go out — the console fills with
      // "Unregister rejected with status code 401" and, worse, the contact stays
      // bound on the PBX after the tab closes, so inbound keeps ringing a browser
      // that isn't there.
      //
      // unregister()'s own promise resolves when the request is SENT, not when
      // it is answered, so wait on the request delegate instead. The timeout
      // stops a dead PBX from leaking a UserAgent that never shuts down.
      void (async () => {
        try {
          await new Promise<void>((resolve) => {
            const done = () => resolve();
            window.setTimeout(done, 2000);
            reg?.unregister({ requestDelegate: { onAccept: done, onReject: done } })
              .catch(done);
            if (!reg) done();
          });
        } catch { /* ignore */ }
        try { await agent?.stop(); } catch { /* ignore */ }
      })();
      if (audioRef.current) { audioRef.current.remove(); audioRef.current = null; }
    };
  }, [mock, config]);

  // ── is the microphone usable? ─────────────────────────────────────────────
  // ASK AT SIGN-IN, NOT AT THE FIRST CALL. Until now the browser was only asked
  // for the mic inside invite()/accept(), so a refused mic surfaced as a failed
  // call — or, inbound, as nothing at all — while the desk read "Ready for the
  // next call" and the queue kept routing customers here (TC-A01.2).
  //
  // Once registered: read the permission, follow its changes (the agent can
  // flip it in the address bar at any time), and if it has not been answered
  // yet, ask now with a throwaway getUserMedia so the answer is known before a
  // customer rings. The tracks are stopped at once; calls open their own.
  useEffect(() => {
    if (mock || !registered) return;
    let cancelled = false;
    let perm: PermissionStatus | null = null;
    const onChange = () => { if (!cancelled && perm) setMicStatus(micStatusFromPermission(perm.state)); };

    const probe = async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setMicStatus("blocked"); return; }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        stream.getTracks().forEach((t) => t.stop());
        if (!cancelled) setMicStatus("granted");
      } catch (e) {
        if (!cancelled && isMicUnavailableError(e)) setMicStatus("blocked");
      }
    };

    void (async () => {
      let state: MicStatus = "unknown";
      try {
        // "microphone" is not in TypeScript's PermissionName union everywhere.
        perm = await navigator.permissions.query({ name: "microphone" as PermissionName });
        if (cancelled) return;
        state = micStatusFromPermission(perm.state);
        perm.addEventListener("change", onChange);
      } catch { /* no Permissions API for the mic (older Firefox/Safari) — probe instead */ }
      if (cancelled) return;
      setMicStatus(state);
      if (state === "unknown") await probe();
    })();

    return () => { cancelled = true; perm?.removeEventListener("change", onChange); };
  }, [mock, registered]);

  // ── actions ─────────────────────────────────────────────────────────────
  const placeCall = useCallback(async (number: string, name?: string) => {
    const info: CallInfo = { number, name, direction: "out" };
    if (mock) { setCall(info); setStatus("oncall"); setMuted(false); setHeld(false); return; }
    const ua = uaRef.current;
    if (!ua) return;
    const target = UserAgent.makeURI(`sip:${number}@${(config as SipConfig).domain}`);
    if (!target) { setError("Invalid dial target"); return; }

    // Mint the call id HERE, before the INVITE, so it can travel to Asterisk as
    // a SIP header. That header is what lets PBX-side recording (MixMonitor)
    // name its file after the Helix interaction — otherwise Asterisk only knows
    // its own UNIQUEID, Helix only knows this id, and nothing can correlate the
    // two without guessing from timestamps.
    const callId = `sp_${crypto.randomUUID()}`;
    const inviter = new Inviter(ua, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      extraHeaders: [`X-Helix-Call-Id: ${callId}`],
    });
    wireSession(inviter, info, callId);
    setStatus("ringing");   // outbound stays "ringing" until the callee answers (Established)
    try {
      // invite() builds a WebRTC offer — this is where a denied microphone
      // (getUserMedia) throws, so surface it instead of failing silently.
      await inviter.invite();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (isMicUnavailableError(e)) {
        setMicStatus("blocked");
        setError(MIC_BLOCKED_MESSAGE);
      } else {
        setError("Call failed: " + msg);
      }
      setStatus("ready");
      setCall(null);
    }
  }, [mock, config, wireSession]);

  const answer = useCallback(async () => {
    const inc = incoming;
    stopRing();
    setIncoming(null);
    if (mock) { if (inc) { setCall(inc); setStatus("oncall"); } return; }
    const invitation = invitationRef.current;
    if (!invitation || !inc) return;
    // Adopt the PBX's id when it sent one; otherwise wireSession mints its own
    // (older dialplan, or a leg that bypassed [from-trunk]).
    wireSession(invitation, inc, inboundCallIdRef.current ?? undefined);
    inboundCallIdRef.current = null;
    // Hand ownership to wireSession. The onInvite listener treats "still the
    // current invitation on Terminated" as "never answered" and reports a missed
    // call; leaving this set would mark every ANSWERED inbound call as missed the
    // moment it ended.
    invitationRef.current = null;
    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
    } catch (e) {
      // accept() is where a blocked mic throws on an inbound call. It used to
      // be unhandled: the ringing popup had already gone, so the agent saw
      // nothing and the customer was left on a call nobody could speak into.
      if (isMicUnavailableError(e)) {
        setMicStatus("blocked");
        setError(MIC_BLOCKED_MESSAGE);
      } else {
        setError("Could not answer: " + (e instanceof Error ? e.message : String(e)));
      }
      // Give the caller back to the queue rather than holding a dead leg.
      if (invitation.state === SessionState.Initial || invitation.state === SessionState.Establishing) {
        try { await invitation.reject(); } catch { /* already gone */ }
      }
      setCall(null);
      setStatus("ready");
    }
  }, [mock, incoming, wireSession]);

  const reject = useCallback(() => {
    // SAY WHO DECLINED, AND FROM WHERE.
    //
    // Declining hangs up on a customer, and until now it left no trace: the only
    // evidence was a 480 on the wire, identical whether a person pressed Decline
    // or some stray handler fired. That ambiguity cost an afternoon — a
    // click-anywhere backdrop was rejecting calls and looked exactly like a
    // switch fault. A one-line stack here answers "who did this" immediately.
    console.warn("[softphone] reject() called — declining the incoming call\n" +
      (new Error().stack ?? "(no stack)"));
    stopRing();
    setIncoming(null);
    if (mock) return;
    try { settle(invitationRef.current?.reject()); } catch { /* ignore */ }
    invitationRef.current = null;

    // DECLINING MUST MEAN SOMETHING ON THIS SIDE TOO.
    //
    // A 480 tells the QUEUE "not me" — it does not hang up the caller, and it
    // must not: one agent refusing a call cannot be allowed to disconnect
    // somebody who rang for help. The queue therefore offers the call to the
    // next available agent. With a full team that is exactly right. With ONE
    // ready agent, "the next available agent" is this same person about two
    // seconds later, and the desk rings again, and again, for the whole queue
    // timeout. Pressing Decline achieved nothing except more ringing.
    //
    // So stand the agent down when they decline. The queue then genuinely has
    // nobody to offer it to and stops re-ringing this desk.
    //
    // AUTOMATICALLY READY AGAIN after DECLINE_PAUSE_MS, because a pause the
    // agent never asked for is a pause they will forget to clear — and an agent
    // silently out of the queue is the failure we removed Asterisk's autopause
    // to avoid. Short enough to be a refusal, not a break.
    // `desk: true`: this desk sends the heartbeat (server/ops/desk-heartbeat.ts).
    void opsFetch("/ops/api/voice/softphone/presence", {
      method: "POST",
      json: { presence: "paused", pauseReason: "unspecified", desk: true },
    }).then(() => {
      window.setTimeout(() => {
        void opsFetch("/ops/api/voice/softphone/presence", {
          method: "POST",
          json: { presence: "ready", desk: true },
        }).catch(() => { /* the agent can click Ready themselves */ });
      }, DECLINE_PAUSE_MS);
    }).catch(() => { /* declining still worked; the pause is the extra */ });
  }, [mock]);

  // End the call — whatever KIND of call it is.
  //
  // This used to handle exactly two cases: an established session (BYE) and an
  // outbound leg still ringing (CANCEL, via `instanceof Inviter`). An INBOUND
  // leg is an `Invitation`, never an `Inviter`, so an inbound call that had not
  // yet reached Established matched neither branch and this function did
  // NOTHING — silently, because the catch swallowed everything.
  //
  // Two failures followed from that one gap, and they looked unrelated:
  //   * No BYE reached the PBX, so the caller stayed connected while the agent
  //     had already "hung up".
  //   * No Terminated event ever fired, so `call` was never cleared, the wrap-up
  //     never opened, and the call could not be dispositioned at all.
  //
  // Every branch below ends with a request actually being sent. If none can be —
  // the one real gap, see the catch — the UI deliberately stays on-call rather
  // than claiming an end that did not happen.
  const hangup = useCallback(() => {
    if (mock) { setCall(null); setStatus("wrap"); return; }
    const s = sessionRef.current;

    // Still alerting: nothing has been wired yet, so there is no session to end
    // — only an invitation to decline. Without this the agent's only exit from a
    // ringing call was to let the PBX time the Dial out.
    if (!s) {
      const inv = invitationRef.current;
      invitationRef.current = null;
      stopRing();
      setIncoming(null);
      setStatus((cur) => (cur === "ringing" ? "ready" : cur));
      try { settle(inv?.reject()); } catch { /* caller already gave up */ }
      return;
    }

    try {
      if (s.state === SessionState.Established) { settle(s.bye()); return; }
      // Outbound, not yet answered — CANCEL the INVITE we sent.
      if (s instanceof Inviter) { settle(s.cancel()); return; }
      // Inbound, not yet established — decline it. BYE is illegal before the
      // dialog is confirmed and SIP.js throws, which is how this went unnoticed.
      if (s instanceof Invitation) { settle(s.reject()); return; }
    } catch {
      // Accepted-but-not-yet-confirmed is a real gap in SIP.js: reject() is no
      // longer legal and bye() is not yet. By the time this lands the dialog has
      // usually confirmed, so BYE is the right second attempt.
      try { settle(s.bye()); } catch { /* nothing further we can send */ }
    }
  }, [mock]);

  const setMute = useCallback((on: boolean) => {
    setMuted(on);
    if (mock) return;
    const pc = sessionRef.current && peerConn(sessionRef.current);
    pc?.getSenders().forEach((snd) => { if (snd.track) snd.track.enabled = !on; });
  }, [mock]);

  // REAL hold — a re-INVITE that renegotiates the media direction, not a local
  // mute. The difference matters to the person on the other end: muting only
  // stops our audio, so they hear SILENCE and cannot tell they were held, and
  // the PBX has no idea either — so it can't play music-on-hold and the call
  // still looks active to Asterisk.
  //
  // Setting the transceiver direction and re-inviting sends a=sendonly (hold)
  // / a=sendrecv (resume) in the SDP, which is what the far end and the PBX
  // actually act on.
  /** Hold or resume ONE session. Extracted so the second line uses exactly the
   *  same re-INVITE the first one does — a second implementation would be a
   *  second set of edge cases, and this one already handles the rejected
   *  re-INVITE that a busy PBX produces.
   *
   *  `audible` is separate from `on` because holding is about the FAR END and
   *  audibility is about this agent's ear. With two calls up, the held party
   *  must hear hold music (so: re-INVITE) while the agent hears only the line
   *  they switched to (so: which stream the audio element carries). Conflating
   *  them is how you get an agent listening to two conversations at once. */
  const applyHold = useCallback(async (session: Session | null, on: boolean, audible: boolean) => {
    const pc = session && peerConn(session);
    if (!session || !pc) return false;
    try {
      pc.getTransceivers().forEach((t) => {
        if (t.sender.track?.kind === "audio" || t.receiver.track?.kind === "audio") {
          t.direction = on ? "sendonly" : "sendrecv";
        }
      });
      // `hold` IS THE PART THE WIRE SEES. Setting transceiver directions above
      // looks like it should put a=sendonly in the offer, but sip.js's web
      // session-description-handler rebuilds the SDP from its own options and
      // ignores hand-set directions — so every re-INVITE went out sendrecv,
      // the PBX never learned about any hold, and no caller ever heard hold
      // music. Found by grepping the switch's log for "sendonly": zero
      // occurrences across the entire file, in any test, ever.
      await (session as unknown as { invite: (o?: unknown) => Promise<unknown> }).invite({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false }, hold: on },
      });
      pc.getSenders().forEach((snd) => { if (snd.track) snd.track.enabled = !on; });
      if (audioRef.current) audioRef.current.muted = !audible;
      return true;
    } catch (e) {
      pc.getSenders().forEach((snd) => { if (snd.track) snd.track.enabled = !on; });
      if (audioRef.current) audioRef.current.muted = !audible;
      console.warn("[softphone] hold re-INVITE failed:", e);
      return false;
    }
  }, []);

  const setHold = useCallback(async (on: boolean) => {
    if (mock) { setHeld(on); return; }
    const session = sessionRef.current;
    const pc = session && peerConn(session);
    if (!session || !pc) return;
    try {
      pc.getTransceivers().forEach((t) => {
        if (t.sender.track?.kind === "audio" || t.receiver.track?.kind === "audio") {
          t.direction = on ? "sendonly" : "sendrecv";
        }
      });
      // Re-INVITE carrying the hold. The `hold` option is what makes the SDH
      // write a=sendonly into the offer — see the long note in applyHold: the
      // hand-set transceiver directions alone never reached the wire.
      await (session as unknown as { invite: (o?: unknown) => Promise<unknown> }).invite({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false }, hold: on },
      });
      setHeld(on);
      // Stop our own outbound audio too, so the agent isn't heard while held.
      pc.getSenders().forEach((snd) => { if (snd.track) snd.track.enabled = !on; });
      if (audioRef.current) audioRef.current.muted = on;
    } catch (e) {
      // A re-INVITE can be rejected (or race another one). Fall back to the
      // local mute so the agent still gets privacy, and say so rather than
      // silently showing "Held" when the far end was never told.
      pc.getSenders().forEach((snd) => { if (snd.track) snd.track.enabled = !on; });
      if (audioRef.current) audioRef.current.muted = on;
      setHeld(on);
      setError("Hold was applied locally only — the PBX rejected the re-INVITE, so no hold music.");
      console.warn("[softphone] hold re-INVITE failed:", e);
    }
  }, [mock]);

  /** Move the agent between the two live calls.
   *
   *  ── WHY THIS IS NOT JUST "HOLD THE OTHER ONE" ─────────────────────────────
   *  Three things have to move together, and doing any two of them is a bug the
   *  agent will not notice until a customer complains:
   *
   *    the far ends   — each party must be held or resumed with a real
   *                     re-INVITE, so the held one gets music and the PBX knows
   *    the microphone — the agent must be heard by exactly one of them
   *    the earpiece   — the audio element carries one stream, so it has to be
   *                     re-pointed or the agent keeps hearing the party they
   *                     just left
   *
   *  The last one is the easy one to miss: without it the switch LOOKS right on
   *  screen while the agent is still listening to the wrong call.
   *
   *  Resume before hold would briefly have both parties live and hearing each
   *  other, which is a privacy failure — a customer overhearing the internal
   *  consultation about them. So the outgoing line is held FIRST, and the
   *  incoming one resumed only after.
   */
  const switchLine = useCallback(async (to: "customer" | "consult") => {
    if (mock) { setActiveLine(to); return; }
    const customer = sessionRef.current;
    const consultSession = consultRef.current;
    if (to === "consult" && !consultSession) return;
    if (activeLine === to) return;

    const leaving = to === "customer" ? consultSession : customer;
    const arriving = to === "customer" ? customer : consultSession;
    if (!arriving) return;

    await applyHold(leaving, true, false);
    const resumed = await applyHold(arriving, false, true);
    // Re-point the earpiece regardless: even when the re-INVITE was refused the
    // agent should hear the line they chose, and applyHold has already fallen
    // back to a local hold for the other one.
    attachMedia(arriving);
    setActiveLine(to);
    // `held` describes the CUSTOMER, which is what the desk shows and what the
    // wrap-up rules read. Consulting means the customer is held, by definition.
    setHeld(to === "consult");
    if (!resumed) {
      setError("Switched lines, but the PBX refused the re-INVITE — the other party may not hear hold music.");
    }
  }, [mock, activeLine, applyHold, attachMedia]);

  const sendDtmf = useCallback((digit: string) => {
    if (mock) return;
    const sdh = sessionRef.current?.sessionDescriptionHandler as unknown as {
      sendDtmf?: (tone: string) => boolean;
    } | undefined;
    sdh?.sendDtmf?.(digit);
  }, [mock]);

  // Blind transfer: REFER the call to `target` and drop off. The target may be
  // another extension or an outside number — both are just a SIP URI on the PBX
  // domain, and the dialplan decides which.
  //
  // Reported as `call.transferred`, NOT as an escalation: the platform records
  // this on its own transferred_to_ext/transferred_at columns and leaves
  // containment_status alone, because that column is the AI's "did the bot
  // finish this without a human" metric (see shared/schema-voice.ts).
  const transfer = useCallback((target: string) => {
    const dest = target.trim();
    if (!dest) return;
    if (mock) return;
    const s = sessionRef.current;
    const uri = UserAgent.makeURI(`sip:${dest}@${(config as SipConfig).domain}`);
    if (!s || !uri) { setError("Invalid transfer target"); return; }
    try {
      // A REJECTED refer() IS A FAILED TRANSFER, not noise — unlike bye/cancel,
      // which lose a harmless race with a dialog that was ending anyway. The
      // agent has told the caller they are being put through, so this one has to
      // reach them; without the catch it was an unhandled rejection and the UI
      // reported success.
      void Promise.resolve(s.refer(uri)).catch((e: unknown) => {
        setError("Transfer failed: " + (e instanceof Error ? e.message : String(e)));
      });
      const callId = callIdRef.current;
      if (callId && config?.ext) {
        reportCall({ event: "call.transferred", callId, ext: config.ext, target: dest, transferType: "blind" });
      }
    } catch (e) {
      setError("Transfer failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }, [mock, config]);


  // Warm (attended) transfer + conference.

  /** Audio sink for the consultation leg — separate from the primary call's. */
  const consultAudio = useCallback((): HTMLAudioElement => {
    if (!consultAudioRef.current) {
      const el = document.createElement("audio");
      el.autoplay = true;
      el.hidden = true;
      document.body.appendChild(el);
      consultAudioRef.current = el;
    }
    return consultAudioRef.current;
  }, []);

  /**
   * Put the caller on hold and call someone else.
   *
   * Hold FIRST, and hold for real (the re-INVITE with sendonly that setHold
   * sends) — otherwise the caller hears the whole consultation, including
   * whatever the agent says about them.
   */
  const startConsult = useCallback(async (target: string, name?: string) => {
    const dest = target.trim();
    if (!dest) return;
    if (mock) { setConsult({ number: dest, name, direction: "out" }); setConsultLive(true); return; }
    const ua = uaRef.current;
    if (!ua || !config) return;
    if (!sessionRef.current) { setError("No active call to transfer"); return; }
    if (consultRef.current) { setError("Already consulting someone"); return; }

    const uri = UserAgent.makeURI("sip:" + dest + "@" + config.domain);
    if (!uri) { setError("Cannot call " + dest); return; }

    try { await setHold(true); } catch { /* consult anyway; worst case they hear it */ }

    const inviter = new Inviter(ua, uri, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    consultRef.current = inviter;
    setConsult({ number: dest, name, direction: "out" });
    setConsultLive(false);

    inviter.stateChange.addListener((st: SessionState) => {
      if (st === SessionState.Established) {
        setConsultLive(true);
        const pc = peerConn(inviter);
        if (pc) {
          const stream = new MediaStream();
          pc.getReceivers().forEach((r) => r.track && stream.addTrack(r.track));
          consultAudio().srcObject = stream;
        }
      } else if (st === SessionState.Terminated) {
        // Only clear if THIS is still the current consultation. A completed
        // transfer terminates it too, and that path has already moved on.
        if (consultRef.current === inviter) {
          consultRef.current = null;
          setConsult(null);
          setConsultLive(false);
        }
      }
    });

    try {
      await inviter.invite();
    } catch (e) {
      consultRef.current = null;
      setConsult(null);
      setConsultLive(false);
      setError("Could not reach " + dest + ": " + (e instanceof Error ? e.message : String(e)));
      await setHold(false).catch(() => {});
    }
  }, [mock, config, setHold, consultAudio]);

  /**
   * Connect the caller to the colleague and drop out.
   *
   * REFER **with Replaces** — passing the consultation SESSION rather than a URI
   * is what makes this attended. It tells the caller's phone to replace its call
   * with the one already established, so the colleague is not rung a second time
   * and the conversation carries on uninterrupted.
   */
  const completeTransfer = useCallback(() => {
    if (mock) { setConsult(null); setConsultLive(false); setCall(null); setStatus("wrap"); return; }
    const primary = sessionRef.current;
    const cons = consultRef.current;
    if (!primary || !cons) { setError("Nothing to transfer"); return; }
    try {
      // Same reasoning as the blind transfer: a rejection here means the caller
      // was NOT handed over, and the agent has already said they would be.
      void Promise.resolve(primary.refer(cons)).catch((e: unknown) => {
        setError("Transfer failed: " + (e instanceof Error ? e.message : String(e)));
      });
      const callId = callIdRef.current;
      if (callId && config?.ext) {
        reportCall({
          event: "call.transferred", callId, ext: config.ext,
          target: consult?.number ?? "", transferType: "attended",
        });
      }
      consultRef.current = null;
      setConsult(null);
      setConsultLive(false);
    } catch (e) {
      setError("Transfer failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }, [mock, config, consult]);

  /** Abandon the consultation and pick the caller back up. */
  const cancelConsult = useCallback(async () => {
    const cons = consultRef.current;
    consultRef.current = null;
    setConsult(null);
    setConsultLive(false);
    if (!mock && cons) {
      try {
        if (cons.state === SessionState.Established) settle(cons.bye());
        else if (cons instanceof Inviter) settle(cons.cancel());
      } catch { /* already gone */ }
    }
    // The caller has been on hold through all of this; taking them off hold is
    // the entire point of cancelling.
    if (!mock) await setHold(false).catch(() => {});
  }, [mock, setHold]);

  /**
   * Merge caller, colleague and agent into one conference.
   *
   * Mixed on the PBX (ConfBridge), not in the browser. Browser mixing only lasts
   * while a browser is a party to the call, so it would produce nothing for an
   * AI leg and would collapse the moment the agent stepped out — and it would
   * silently stop the recording, the same reason recording lives on Asterisk.
   *
   * Both parties are REFERred into the room and the agent dials in after them.
   * ConfBridge holds whoever arrives first, so arrival order does not matter.
   */
  const conference = useCallback(async () => {
    if (mock) { setConsult(null); setConsultLive(false); return; }
    const ua = uaRef.current;
    const primary = sessionRef.current;
    const cons = consultRef.current;
    if (!ua || !primary || !cons || !config) { setError("Need both calls to conference"); return; }

    // 7 digits starting with 8 — a length no other dialplan pattern matches, so
    // a room can never be mistaken for a number and dialled out to a stranger.
    const room = "8" + String(Math.floor(100000 + Math.random() * 900000));
    const uri = UserAgent.makeURI("sip:" + room + "@" + config.domain);
    if (!uri) { setError("Could not create conference room"); return; }

    // ORDER MATTERS: WE join the room first, and only move the caller once we
    // are actually in it.
    //
    // The first version did the reverse — REFER both parties into the room, then
    // dial in ourselves 1.2s later on a setTimeout. A REFER cannot be taken
    // back, so when that dial-in did not happen the caller was left ALONE in an
    // empty room: no audio, no agent, no timeout, nothing to do but hang up. It
    // happened on a real call (room 8528088 held the caller and nobody else).
    //
    // Joining first makes the failure safe. If we cannot get into the room, the
    // caller has not been moved anywhere — they are still on hold with us, and
    // the agent can simply carry on or try again.
    const joiner = new Inviter(ua, uri, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    const joined = new Promise<boolean>((resolve) => {
      let settled = false;
      joiner.stateChange.addListener((st: SessionState) => {
        if (settled) return;
        if (st === SessionState.Established) { settled = true; resolve(true); }
        else if (st === SessionState.Terminated) { settled = true; resolve(false); }
      });
    });

    try {
      await joiner.invite();
    } catch (e) {
      setError("Could not open the conference: " + (e instanceof Error ? e.message : String(e)));
      return;   // nobody moved
    }
    if (!(await joined)) {
      setError("Could not open the conference — nobody was moved, the caller is still with you.");
      return;
    }

    // We are in the room. Now it is safe to bring the other two across.
    try {
      // Both legs move into the room. A rejection on either means someone was
      // left behind, which the agent must hear about — the catch below only sees
      // a synchronous throw.
      const moved = (e: unknown) =>
        setError("Could not move everyone into the conference: " + (e instanceof Error ? e.message : String(e)));
      void Promise.resolve(primary.refer(uri)).catch(moved);
      void Promise.resolve(cons.refer(uri)).catch(moved);
      const callId = callIdRef.current;
      if (callId && config.ext) {
        reportCall({
          event: "call.transferred", callId, ext: config.ext,
          target: room, transferType: "conference",
        });
      }
    } catch (e) {
      // We are in the room and they are not. Say so plainly rather than leave
      // the agent guessing why nobody can hear them.
      setError("Conference opened but the parties could not be moved: " + (e instanceof Error ? e.message : String(e)));
    }

    consultRef.current = null;
    setConsult(null);
    setConsultLive(false);
    // The room is the agent's call now.
    wireSession(joiner, { number: room, name: "Conference", direction: "out" });
  }, [mock, config, wireSession]);

  const simulateIncoming = useCallback((info: CallInfo) => {
    if (!mock) return;              // real inbound arrives via the onInvite delegate
    setIncoming({ ...info, direction: "in" });
    setStatus("ringing");
  }, [mock]);

  const clearError = useCallback(() => setError(null), []);

  return {
    status, registered, mock, error, call, incoming, muted, held, mediaLost, ringerSilent, micStatus, clearError, liveTurns,
    assistConversationId,
    currentCallId, wrapUpCallId,
    placeCall, answer, reject, hangup, setMute, setHold, sendDtmf, transfer,
    consult, consultLive, startConsult, completeTransfer, cancelConsult, conference,
    activeLine, switchLine,
    simulateIncoming,
  };
}
